import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { getTradeAveragesByDate } from "../../../services/Axios";
import { calculateSpread, spreadByDate } from "./tradeAverages";
import {
  buttonSx,
  describeFilters,
  formatPrice,
  formatQty,
} from "./averagesShared";

const SymbolSelect = ({ label, symbols, value, onChange }) => (
  <Grid item xs={12} sm={6}>
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        <MenuItem value="">None</MenuItem>
        {symbols.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
);

// The spread broken down by trade date, shown under the overall result.
function SpreadByDate({ state }) {
  if (state.status === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (state.status === "error") {
    return (
      <Alert severity="error" sx={{ mt: 1.5 }}>
        {state.error}
      </Alert>
    );
  }
  if (state.status !== "done") return null;
  if (state.rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ mt: 1.5 }}>
        No dated trades for these two securities.
      </Typography>
    );
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700 }}>
        By date
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        Each date uses only that day's trades. A date where only one leg traded
        has no spread.
      </Typography>
      <Box sx={{ overflow: "auto", maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Target avg</TableCell>
              <TableCell align="right">Acquirer avg</TableCell>
              <TableCell align="right">Spread</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.rows.map((row) => (
              <TableRow key={row.date} hover>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right">{formatPrice(row.targetAvg)}</TableCell>
                <TableCell align="right">{formatPrice(row.acquirerAvg)}</TableCell>
                <TableCell align="right">
                  <strong>{formatPrice(row.spread)}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

const NO_DATES = { status: "idle", rows: [], error: null };

// Average price per spread: pick the target and the acquirer from the
// securities in the current result, enter cash and ratio, press Calculate.
export default function SpreadDialog({ open, onClose, averages, query, filters }) {
  const theme = useTheme();
  const {
    canAverage,
    loading,
    error,
    rows,
    symbols,
    totalTrades,
    includedTrades,
    isStale,
    loaded,
    load,
  } = averages;

  const [targetSymbol, setTargetSymbol] = useState("");
  const [acquirerSymbol, setAcquirerSymbol] = useState("");
  const [cash, setCash] = useState("");
  const [ratio, setRatio] = useState("");
  const [result, setResult] = useState(null);
  const [byDate, setByDate] = useState(NO_DATES);
  // Ignores a by-date answer that arrives after the inputs were changed.
  const dateRequest = useRef(0);

  const chips = useMemo(() => describeFilters(query, filters), [query, filters]);

  // Any input change invalidates the shown result until Calculate is pressed.
  const edit = (setter) => (value) => {
    setter(value);
    setResult(null);
    dateRequest.current += 1;
    setByDate(NO_DATES);
  };

  const handleCalculate = async () => {
    const outcome = calculateSpread({
      rows,
      targetSymbol,
      acquirerSymbol,
      cash,
      ratio,
    });
    setResult(outcome);
    const request = ++dateRequest.current;
    if (outcome.error) {
      setByDate(NO_DATES);
      return;
    }
    setByDate({ status: "loading", rows: [], error: null });
    try {
      const dateRows = await getTradeAveragesByDate(query, filters, [
        targetSymbol,
        acquirerSymbol,
      ]);
      if (request !== dateRequest.current) return;
      setByDate({
        status: "done",
        rows: spreadByDate({ dateRows, targetSymbol, acquirerSymbol, cash, ratio }),
        error: null,
      });
    } catch (err) {
      if (request !== dateRequest.current) return;
      setByDate({
        status: "error",
        rows: [],
        error:
          err?.response?.data?.detail ||
          `Failed to load the by-date breakdown: ${err.message}`,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        component="div"
        sx={{ pb: 1, fontSize: "1.25rem", fontWeight: 700 }}
      >
        Average price per spread
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
          {chips.map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" />
          ))}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && isStale && (
          <Alert
            severity="warning"
            sx={{ mb: 1 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={load}
                disabled={!canAverage}
              >
                Refresh
              </Button>
            }
          >
            Filters changed since these averages were calculated.
          </Alert>
        )}

        {!loading && !error && loaded && (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Spread price = cash + ratio x Leg 2 (Acquirer) average - Leg 1
              (Target) average.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ mb: 1.5 }}
            >
              Each average combines every Buy, Sell, Short and Cover trade of
              that security across all pages of the current filters (
              {formatQty(includedTrades)} of {formatQty(totalTrades)} trades).
            </Typography>

            <Grid container spacing={1.5}>
              <SymbolSelect
                label="Leg 1 (Target)"
                symbols={symbols}
                value={targetSymbol}
                onChange={edit(setTargetSymbol)}
              />
              <SymbolSelect
                label="Leg 2 (Acquirer)"
                symbols={symbols}
                value={acquirerSymbol}
                onChange={edit(setAcquirerSymbol)}
              />
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cash"
                  type="number"
                  value={cash}
                  onChange={(e) => edit(setCash)(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ratio"
                  type="number"
                  value={ratio}
                  onChange={(e) => edit(setRatio)(e.target.value)}
                />
              </Grid>
            </Grid>

            <Button
              fullWidth
              variant="contained"
              startIcon={<PlayArrowIcon />}
              sx={{ ...buttonSx, mt: 1.5 }}
              onClick={handleCalculate}
              disabled={rows.length === 0}
            >
              Calculate
            </Button>

            {result?.error && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {result.error}
              </Alert>
            )}
            {result && !result.error && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.grey[100],
                }}
              >
                <Typography variant="body2">
                  Leg 1 (Target) {targetSymbol} average:{" "}
                  <strong>{formatPrice(result.targetAvg)}</strong>
                </Typography>
                <Typography variant="body2">
                  Leg 2 (Acquirer) {acquirerSymbol} average:{" "}
                  <strong>{formatPrice(result.acquirerAvg)}</strong>
                </Typography>
                <Typography
                  sx={{ mt: 0.5, fontSize: "1.25rem", fontWeight: 700 }}
                >
                  Spread price: {formatPrice(result.spread)}
                </Typography>
              </Box>
            )}
            {result && !result.error && <SpreadByDate state={byDate} />}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading || !canAverage}
        >
          Refresh
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
